/**
 * Site header — crest lockup, section nav, phone, booking CTA.
 *
 * ─────────────────────────────────────────────────────────────
 * ROUTES vs ANCHORS
 * ─────────────────────────────────────────────────────────────
 * Barbers, Gallery, Walk-ins and Visit are sections of the homepage, so they
 * are anchors. Book is NOT — the wizard is a real route at /book, and an
 * href="#book" would be a dead primary CTA since no section carries that id.
 *
 * The anchors are plain <a> with the locale-prefixed path in front of the
 * hash (`/es/#gallery`). On the homepage the browser treats that as a
 * same-document scroll; from /about or /services it navigates home and then
 * scrolls. A router Link would need a scroll-to-hash handler that does not
 * exist yet, and adding one is outside this commit.
 *
 * The drawer renders as a SIBLING of <header>, not inside it. The header
 * carries backdrop-blur, and backdrop-filter makes an ancestor a containing
 * block for position:fixed descendants — a drawer nested inside it gets
 * clipped to the header's own box instead of covering the viewport.
 *
 * Facts — shop name, city, founded year, phone — resolve from
 * shop.config.json. Labels come from the copy dictionaries so /es renders in
 * Spanish. Colours are palette tokens, not raw hex, so the "change three
 * hexes to reskin for another shop" property in tailwind.config survives.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LocaleToggle from './LocaleToggle';
import { useCopy, useLocale, localePrefix } from '../../content';
import { useShopConfig } from '../../hooks/useShopConfig';

export default function Header() {
  const copy = useCopy();
  const { t, shop } = useShopConfig();
  const p = localePrefix(useLocale());
  const [open, setOpen] = useState(false);

  const sections = [
    { href: `${p}/#menu`, label: copy.nav.menu },
    { href: `${p}/#barbers`, label: copy.nav.barbers },
    { href: `${p}/#gallery`, label: copy.nav.gallery },
    { href: `${p}/#visit`, label: copy.nav.visit },
  ];

  // A drawer that outlives the scroll position it covers is worse than no
  // drawer, so lock the body while it is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const bookCta = t(copy.home.hero.ctaPrimary);

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-edge-1 bg-paper/[0.93] backdrop-blur">
        <div className="flex items-center justify-between gap-3 px-4 py-[15px] md:px-10 md:py-5">
          <Link
            to={`${p}/`}
            className="flex shrink-0 items-center gap-3 md:gap-3.5"
            onClick={() => setOpen(false)}
          >
            {/* Square variant: the primary crest sits inside 1024x1536 of
                canvas padding, so at lockup sizes the art renders smaller than
                its box. The trimmed file fills the box instead. */}
            <img
              src="/assets/logo/kd-crest-square.png"
              alt={shop.name}
              width={56}
              height={56}
              className="block h-12 w-12 object-contain sm:h-14 sm:w-14"
            />
            <span className="flex flex-col gap-[3px]">
              <span className="whitespace-nowrap font-newsreader text-[18px] font-semibold leading-[1.1] text-navy md:text-[22px]">
                {shop.name}
              </span>
              {/* The eyebrow is the first thing to go: at 390px it is what
                  forces the lockup to wrap, and it repeats the Visit section. */}
              <span className="hidden whitespace-nowrap font-oswald text-[10px] uppercase tracking-[0.18em] text-warm-muted sm:block">
                {t(copy.tagline.header)}
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            <a
              href={shop.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-newsreader text-[19px] text-slate-body transition-colors hover:text-navy"
            >
              {t(copy.nav.book)}
            </a>
            {sections.map((s) => (
              <a
                key={s.href}
                href={s.href}
                className="font-newsreader text-[19px] text-slate-body transition-colors hover:text-navy"
              >
                {t(s.label)}
              </a>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-2 md:gap-5">
            {/* Before Book, not after: at 390px the Book pill and the burger
                are the two things a thumb reaches for, and inserting between
                them is what pushed the header to two rows. */}
            <LocaleToggle />
            <a
              href={`tel:${shop.phone_e164}`}
              className="hidden whitespace-nowrap font-newsreader text-[19px] text-slate-body transition-colors hover:text-navy lg:inline"
            >
              {shop.phone_display}
            </a>
            {/* Short label on mobile — the full CTA wraps inside the pill at
                390px and balloons the header to three rows. */}
            <a
              href={shop.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap rounded-pill bg-navy px-4 py-2.5 font-newsreader text-[15px] text-white transition-colors hover:bg-red-accent md:px-8 md:py-4 md:text-[19px]"
            >
              <span className="md:hidden">{t(copy.nav.book)}</span>
              <span className="hidden md:inline">{bookCta}</span>
            </a>
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label={t(copy.nav.openMenu)}
              aria-expanded={open}
              className="-mr-1 flex h-11 w-11 flex-col items-center justify-center gap-[5px] md:hidden"
            >
              <span className="block h-[2px] w-6 bg-navy" />
              <span className="block h-[2px] w-6 bg-navy" />
              <span className="block h-[2px] w-6 bg-navy" />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-50 flex flex-col bg-page md:hidden">
          <div className="flex items-center justify-between border-b border-edge-1 px-6 py-5">
            <span className="font-newsreader text-[22px] font-semibold text-navy">{shop.name}</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t(copy.nav.closeMenu)}
              className="flex h-11 w-11 items-center justify-center font-newsreader text-3xl text-navy"
            >
              ×
            </button>
          </div>

          <nav className="flex flex-col px-6 py-2">
            <a
              href={shop.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="border-b border-edge-1 py-5 font-newsreader text-[26px] text-navy"
            >
              {t(copy.nav.book)}
            </a>
            {sections.map((s) => (
              <a
                key={s.href}
                href={s.href}
                onClick={() => setOpen(false)}
                className="border-b border-edge-1 py-5 font-newsreader text-[26px] text-navy"
              >
                {t(s.label)}
              </a>
            ))}
          </nav>

          <div className="mt-auto flex items-center justify-between border-t border-edge-1 px-6 py-6">
            <span className="font-oswald text-[12px] uppercase tracking-[0.2em] text-warm-muted">
              {t(copy.localeToggle.label)}
            </span>
            <LocaleToggle />
          </div>


          <a
            href={`tel:${shop.phone_e164}`}
            className="mt-auto px-6 pb-10 pt-6 font-newsreader text-[22px] text-slate-body"
          >
            {shop.phone_display}
          </a>
        </div>
      )}
    </>
  );
}
